import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import z from 'zod';

import { Button } from '@/components/Button';
import { ColorPicker } from '@/components/ColorPicker/ColorPicker';
import { Input } from '@/components/Inputs';
import Modal from '@/components/Modal/Modal';
import type { TransactionCategory } from '@/store/expenses-store/transactions/transactions.dto';
import { useTransactionsStore } from '@/store/expenses-store/transactions/transactions.store';
import { toast } from '@/utils/toast.ts';

import s from './CategoryModal.module.scss';

const schema = z.object({
  name: z.string().min(1, 'Please enter a name.'),
});

type CategoryFormData = z.infer<typeof schema>;

export const DEFAULT_CATEGORY_COLOR = '#94A3B8';

interface CategoryModalProps {
  open: boolean;
  onClose: () => void;
  isEdit?: boolean;
  /** The category being edited. Ignored when creating. */
  category?: TransactionCategory | null;
  /** Seeds the name field when creating — the text a picker was already filtering by. */
  initialName?: string;
  onSuccess?: () => void | Promise<void>;
  /** Creation only, and unlike `onSuccess` it hands over the category that was just made. */
  onCreated?: (created: TransactionCategory) => void | Promise<void>;
}

export function CategoryModal({
  open,
  onClose,
  isEdit,
  category,
  initialName,
  onSuccess,
  onCreated,
}: CategoryModalProps) {
  const [color, setColor] = useState(isEdit ? category?.color : DEFAULT_CATEGORY_COLOR);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { createTransactionCategory, updateTransactionCategory } = useTransactionsStore();

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<CategoryFormData>({
    resolver: zodResolver(schema),
    defaultValues: { name: (isEdit ? category?.title : initialName) ?? '' },
  });
  useEffect(() => {
    if (!open) return;

    reset({ name: (isEdit ? category?.title : initialName) ?? '' });
    setColor(isEdit ? (category?.color ?? DEFAULT_CATEGORY_COLOR) : DEFAULT_CATEGORY_COLOR);
  }, [open, isEdit, category, initialName, reset]);

  const closeAndReset = () => {
    reset();
    setColor(DEFAULT_CATEGORY_COLOR);
    onClose();
  };

  const onSubmit = async (data: CategoryFormData) => {
    setIsSubmitting(true);
    try {
      if (isEdit) {
        if (!category) return;
        // The update replaces every column it is given, so the icon has to ride along or
        // the category comes back without one.
        await updateTransactionCategory(
          category.id,
          data.name,
          color as string,
          category.icon || undefined,
        );
        toast.success('Category updated');
      } else {
        const created = await createTransactionCategory(data.name, color as string);
        toast.success('Category created');
        await onCreated?.(created);
      }
      await onSuccess?.();
      closeAndReset();
    } catch (error) {
      // Callers can be waiting on the new category, so a failure has to stay on screen
      // rather than close over a category that was never made.
      toast.error(
        error instanceof Error ? error.message : 'Couldn’t save the category. Please try again.',
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal open={open} onClose={closeAndReset} title={isEdit ? `Edit Category` : 'Add Category'}>
      <form onSubmit={handleSubmit(onSubmit)} className={s.form}>
        <Input
          label="Name"
          placeholder="Provisioning"
          error={errors.name?.message}
          {...register('name')}
        />
        <ColorPicker value={color ?? DEFAULT_CATEGORY_COLOR} onChange={setColor} />
        <Button type="submit" disabled={isSubmitting}>
          {isEdit
            ? isSubmitting
              ? 'Saving…'
              : 'Save Category'
            : isSubmitting
              ? 'Adding…'
              : 'Add Category'}
        </Button>
      </form>
    </Modal>
  );
}
